/**
 * algorithm.service.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { BehaviorSubject, Observable, Subject as RxSubject, Subscription }
  from "rxjs/Rx";

import { ConfigurationService }
  from "../../configuration/configuration.service";
import { SaveableForm } from "../../intake-form/saveable-form";
import { MessageReceiver } from "../../server/message-receiver";
// import { StartAlgorithmRequest, StopAlgorithmRequest, StopAlgorithmResponse }
//   from "../../server/messages/algorithms";
import {
  AcknowledgmentResponse,
  Message,
  MessageTag
} from "../../server/messages/general/";
import { AlgorithmDescriptor, ZoneConfigurationResponse }
  from "../../server/messages/metadata/zone-configuration-response";
import { MessagingService } from "../../server/messaging.service";
import { Subject } from "../../subjects/subject";
import { disconnectEmpty, disconnectValue, disconnectWith }
  from "../../support/types/decorators";
import { valueDefault } from "../../support/utilities/functions";

import { Algorithm, AlgorithmType } from "../structure/algorithm";
import { AlgorithmStep } from "../structure/algorithm-step";
import { RunningAlgorithm } from "../structure/running-algorithm";
import { StepUpdate } from "./pipeline/step-update";

import { TransformerService } from "./pipeline/transformer.service";
import { SubjectContextType } from "../../subjects/subject-context-type";
import { SettingService } from "app/configuration/setting.service";

@Injectable()
export class AlgorithmService implements MessageReceiver
{
  /**
   * Construct a new {@link IntakeStepService}.
   */
  public constructor (
    protected messaging : MessagingService,
    protected router : Router,
    protected config : ConfigurationService,
    protected transformer : TransformerService,
    protected settings : SettingService
  )
  {
    this.messaging.ready.subscribe(
      (on : boolean) => on ? this.connected() : this.disconnected());
    if (this.config.client.type === "admin")
    {
      this.zoneIsAdmin = true;
    }

    this.runningObservable.subscribe(
      (ra : RunningAlgorithm | null) => {
        this.running = ra;
        this.isRunning = !!ra;
      });
  }

  public zoneIsAdmin : boolean = false;

  @disconnectWith("unsubscribe")
  private subscription : Subscription = new Subscription();

  @disconnectWith("next", false)
  public ready : BehaviorSubject<boolean> = new BehaviorSubject(false);

  /**
   * Behavior after the messaging service becomes ready; primarily where we
   * subscribe to incoming non-conversational messages.
   */
  public connected () : void
  {
    // Listen for the ZoneConfigurationResponse and load its data.
    this.subscription = this.messaging.receive(MessageTag.RSP_CFG_ZONE)
      .subscribe((msg : ZoneConfigurationResponse) => {
        // Create the algorithms defined in the message
        this.all = msg.algorithms.map(
          (alg : AlgorithmDescriptor) =>
            new Algorithm(
              alg.name,
              alg.type,
              alg.displayName,
              alg.description,
              undefined,
              alg.supportsIntake,
              alg.useCustomFlow
            ));

        // If support is enabled, add the support algorithms to a special array
        // and demarcate their type as AlgorithmType.BugReporting
        if (msg.supportRequestEnabled)
        {
          this.issueReporting = true;
          this.issueReporters = [];
          this.all.forEach((alg : Algorithm) =>
          this.categorizeByType(alg, msg.supportAlgorithms));
          if (!Array.isArray(this.issueReporters)
            || this.issueReporters.length === 0)
          {
            console.warn("No valid issue reporting algorithms!");
          }
        }
        else
        {
          this.issueReporting = false;
          this.issueReporters = [];
        }

        // Determine if referrals are enabled and load info about the target
        // organization, if available.
        this.referralsEnabled = valueDefault(msg.referrals, true);
        this.referralOrganization =
          valueDefault(msg.referralOrg, "external agency");

        // Extract the standard algorithms from the full list.
        this.standard = this.all.filter(
          (a : Algorithm) => a.type === AlgorithmType.Standard);
        // Extract the context-creating algorithms from the standard list.
        this.createable = this.standard.filter(
          (a : Algorithm) => a.supportsIntake);

        // Store the name of the zone itself.
        this.name = msg.title;

        this.ready.next(true);
      });
  }

  private categorizeByType (
    algorithm : Algorithm,
    supportAlgorithmNames : string[]
  ) : void
  {
    if (supportAlgorithmNames.indexOf(algorithm.name) > -1)
    {
      algorithm.type = AlgorithmType.BugReporting;
      this.issueReporters.push(algorithm);
    }
    else
    {
      algorithm.type = AlgorithmType.Standard;
    }
  }

  public bySCType (sct : SubjectContextType) : Algorithm[]
  {
    return this.all.filter((a : Algorithm) => a.contextType === sct);
  }

  /** Return an Algorithm by its canonical name, or null if not found. */
  public findAlgorithm (name : string) : Algorithm | null
  {
    return this.all.find(
      (alg : Algorithm) => alg.name === name) || null;
  }

  public findLoosely (name : string) : Algorithm | null
  {
    const lc : string = name.toLowerCase();
    // TODO: is locale version well-supported these days?
    return this.all.find(
      (alg : Algorithm) =>
        (alg.displayName.toLowerCase() === lc || alg.name === lc)
    ) || null;
  }

  private static OPEN_SETTING_FOR_SCT : Map<SubjectContextType, string> =
    new Map([
      ["healthcare benefits" as SubjectContextType, "open button default"]
    ]);
  public defaultForOpen (sct : SubjectContextType) : Algorithm | null
  {
    const setting : string = AlgorithmService.OPEN_SETTING_FOR_SCT.get(sct);
    if (setting)
    {
      const name : string = this.settings.get(setting);
      if (name)
      {
        return this.findLoosely(name);
      }
    }
    return null;
  }

  public disconnected () : void {/* decorator-generated */}

  /**
   * All algorithms accessible from this zone, regardless of special types.
   */
  @disconnectEmpty()
  public all : Algorithm[] = [];

  /**
   * All standard intake algorithms. This excludes support algorithms.
   */
  @disconnectEmpty()
  public standard : Algorithm[] = [];

  /**
   * All algorithms that can create contexts. This excludes algorithms that
   * do not support intake.
   */
  @disconnectEmpty()
  public createable : Algorithm[] = [];

  @disconnectValue(false)
  public issueReporting : boolean = false;
  @disconnectEmpty()
  public issueReporters : Algorithm[] = [];

  @disconnectValue(false)
  public referralsEnabled : boolean = false;
  @disconnectValue("unknown")
  public referralOrganization : string = "unknown";

  @disconnectValue("Unconfigured Zone")
  public name : string = "Unconfigured Zone";

  @disconnectValue(false)
  public isRunning : boolean = false;

  public assertRunning () : void
  {
    if (!this.isRunning)
    {
      throw new Error("Method expects active running algorithm; none found!");
    }
  }

  @disconnectValue(null)
  public running : RunningAlgorithm = null;

  public runningObservable : BehaviorSubject<RunningAlgorithm | null> =
    new BehaviorSubject(null);

  public smoothUpdates : Observable<StepUpdate<AlgorithmStep, AlgorithmStep>> =
    this.runningObservable.switchMap(
      (r : RunningAlgorithm | null) =>
        (r === null) ? Observable.never() : r.steps.updates);

  public lockedByComponent : boolean = false;
  public lockingComponent : SaveableForm;
  public lock (component : SaveableForm) : void
  {
    this.lockedByComponent = true;
    this.lockingComponent = component;
    this.running.subject.running = true;
  }

  public unlock () : void
  {
    this.lockedByComponent = false;
    this.lockingComponent = null;
    this.running.subject.running = false;
  }

  /**
   * Request to run an {@link Algorithm} on a {@link Subject}
   */
  private run (algorithm : Algorithm, subject : Subject)
    : Observable<[boolean, boolean]>
  {
    const next : RunningAlgorithm = new RunningAlgorithm(
      algorithm,
      subject,
      this.transformer,
      this.messaging
    );

    const wasLocked : boolean = this.lockedByComponent;
    if (wasLocked)
    {
      console.info("Waiting for unlock by current algorithm", this);
      // The "unlock" is handled by release() -- I just want to take note of it
    }

    return this.release().flatMap(
      (released) => released ? next.start() : [false]
    ).do(
      (ran : boolean) => {
        if (ran)
        {
          this.runningObservable.next(next);
        }
      }
    ).map((ran : boolean) => [ran, wasLocked] as [boolean, boolean]);
  }

  /**
   * Request to run an algorithm and navigate to its intake form.
   * @param algorithm Algorithm to run
   * @param subject Subject to run the algorithm on
   */
  public open (algorithm : Algorithm, subject : Subject) : void
  {
    this.run(algorithm, subject).subscribe(
      ([running, wasLocked] : [boolean, boolean]) => {
        if (running)
        {
          this.navigateToIntake(wasLocked);
        }
      });
  }

  /**
   * Request the router to navigate to the intake for the active algorithm.
   */
  public navigateToIntake (useInterstitial : boolean = false) : void
  {
    if (useInterstitial)
    {
      this.router.navigate(["empty"]).then((success : boolean) => {
        if (success)
        {
          this.router.navigate(this.intakeRoute());
        }
        else
        {
          console.error("Interstitial page load failed!");
        }
      });
    }
    else
    {
      this.router.navigate(this.intakeRoute());
    }
  }

  /**
   * Retrieve a route array for navigating to the intake form.
   */
  private intakeRoute () : any[]
  {
    this.assertRunning();
    if (this.running.algorithm.useCustomFlow
      || (this.config.client.customFlows && this.config.client.customFlows.includes(this.running.algorithm.name))) {
      // TODO: this eventually needs to pick based on the actual value of custom flow, but that will also require us to
      // use dynamic routing (e.g. to a :flowName path) that picks among multiple available custom flow components
      return ["subject", this.running.subject.id, "custom"];
      // return ["custom", "fcwi", this.running.subject.id];
    } else {
      return ["subject", this.running.subject.id, this.running.algorithm.name];
    }
  }

  /**
   * Request an algorithm to stop and release its resources
   */
  public release (target? : RunningAlgorithm) : Observable<boolean>
  {
    if (target && target !== this.running)
    {
      console.warn("Requested release of a different algorithm");
      return Observable.empty();
    }
    if (this.isRunning)
    {
      if (this.lockedByComponent)
      {
        return this.lockingComponent.canLeave().flatMap(
          (leavable : boolean) => leavable ? this.running.stop() : [false]
        ).do((success) => {
          if (success)
          {
            this.unlock();
            this.runningObservable.next(null);
          }
        });
      }
      return this.running.stop().do(() => {
        this.runningObservable.next(null);
      });
    } else {
      console.warn("No active algorithm to release");
      return Observable.of(true);
    }
  }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/algorithms/management/algorithm.service.ts